'use client';

import { Delete as DeleteIcon, History as HistoryIcon } from '@mui/icons-material';
import {
	GridActionsCellItem,
	GridCheckIcon,
	GridCloseIcon,
	GridColDef,
	getGridDateOperators,
	getGridNumericOperators,
} from '@mui/x-data-grid-premium';
import { adaptGridValueCallback } from '@parkchirp/ui-library';
import moment from 'moment-timezone';

import { formatEnumValueForDisplay, getLeaseDetailPageUrl } from '../../../utils';
import { ConfirmationInputModal } from '../../modals';
import { ConversionRightsCell } from './conversionRightsCell';

const dateOperators = getGridDateOperators().filter(
	({ value }) => !['isEmpty', 'isNotEmpty', 'isAnyOf'].includes(value),
);

const numericOperators = getGridNumericOperators().filter(
	({ value }) => !['isEmpty', 'isNotEmpty', 'isAnyOf'].includes(value),
);

const leasingRightsClass = 'leasing-rights-cell';
const leasingCountAndRatesClass = 'leasing-count-and-rate-cell';
const leasingConversionRightsClass = 'leasing-conversion-rights-cell';

export const getLeasingRightsColumns = ({
	setShowDialog,
	rows,
	setRows,
	setShowSnackbar,
	deleteLeasingCountAndRate,
	getUrlToSubscriptionPage,
	readOnly,
	hasComponentAccess,
	handleAuditHistory,
}): GridColDef[] => [
	{
		field: 'actions',
		type: 'actions' as const,
		headerName: 'Actions',
		width: 80,
		cellClassName: 'actions',
		renderCell: (params) => {
			const handleDelete = async () => {
				try {
					const response = await deleteLeasingCountAndRate({
						variables: {
							id: params.row.id,
						},
						context: {
							apiVersion: 'v2',
							appName: process.env.NEXT_PUBLIC_APP_NAME,
						},
					});
					if (response?.data?.deleteCountAndRate) {
						setRows(rows.filter((row) => row.id !== params.row.id));
						setShowSnackbar({
							message: 'Leasing right successfully deleted',
							severity: 'success',
							autoHideDuration: 3000,
						});
					} else {
						setShowSnackbar({
							message: 'Leasing right failed to delete',
							severity: 'error',
							autoHideDuration: 3000,
						});
					}
					setShowDialog(null);
				} catch (err) {
					setShowSnackbar({
						message: err.message,
						severity: 'error',
						autoHideDuration: 3000,
					});
				}
			};

			const actions = [
				<GridActionsCellItem
					key='delete'
					icon={<DeleteIcon />}
					label='Delete'
					onClick={() =>
						setShowDialog(
							<ConfirmationInputModal
								title='Are you sure you want to delete this leasing right?'
								validationString='DELETE'
								dismissText='Cancel'
								confirmText='Confirm'
								handleDismiss={() => setShowDialog(null)}
								handleConfirm={handleDelete}
							/>,
						)
					}
					color='inherit'
				/>,
			];

			if (hasComponentAccess('AuditHistoryTable')) {
				actions.push(
					<GridActionsCellItem
						icon={<HistoryIcon />}
						onClick={() => handleAuditHistory(String(params.row.leasingRightId), params.row.timezone)}
						label='View Audit History'
						key='audit'
						color='inherit'
						data-cy='audit-history-button'
					/>,
				);
			}

			return actions;
		},
	},
	{
		field: 'accountId',
		headerName: 'Acct. ID',
		width: 115,
		renderCell: (params) => {
			if (readOnly) return `AC-${params.row.accountId}`;
			return (
				<a
					style={{ color: 'blue' }}
					target={window.parent.Cypress ? '_self' : '_blank'}
					href={getUrlToSubscriptionPage(
						params.row.subscriptionId,
						params.row.facilityId,
						params.row.accountId,
					)}
					data-cy='account-id-link'
					rel='noreferrer'
				>
					AC-{params.row.accountId}
				</a>
			);
		},
		filterOperators: numericOperators,
	},
	{
		field: 'rightName',
		headerName: 'Right Name',
		type: 'string' as const,
		width: 175,
		renderCell: ({ row }) => (
			<a
				style={{ color: 'blue' }}
				target={window.parent.Cypress ? '_self' : '_blank'}
				href={getLeaseDetailPageUrl(row.leasingRightId)}
				data-cy='lease-link'
				rel='noreferrer'
			>
				{row.rightName}
			</a>
		),
	},
	{
		field: 'facilityNumber',
		headerName: 'Facility Number',
		type: 'string' as const,
		width: 125,
	},
	{
		field: 'facilityName',
		headerName: 'Facility Name',
		type: 'string' as const,
		width: 250,
	},
	{
		field: 'subscriptionName',
		headerName: 'Sub. Name',
		type: 'string' as const,
		width: 175,
	},
	{
		field: 'subscriptionStatus',
		headerName: 'Sub. Status',
		type: 'string' as const,
		sortable: false,
		filterable: false,
	},
	{
		field: 'product',
		headerName: 'Product',
		type: 'singleSelect' as const,
		valueOptions: ['Monthly'],
		valueGetter: adaptGridValueCallback((params) => {
			if (params.value) {
				return formatEnumValueForDisplay(params.value);
			}
			return '';
		}),
		cellClassName: leasingRightsClass,
	},
	{
		field: 'childSubtenantAccountIds',
		headerName: 'Child Subtenant Account IDs',
		type: 'string' as const,
		width: 225,
		sortable: false,
		filterable: false,
		cellClassName: leasingRightsClass,
	},
	{
		field: 'detachedSubtenantAccountIds',
		headerName: 'Detached Subtenant Account IDs',
		type: 'string' as const,
		width: 225,
		sortable: false,
		filterable: false,
		cellClassName: leasingRightsClass,
	},
	{
		field: 'buildings',
		headerName: 'Buildings',
		type: 'string' as const,
		width: 150,
		sortable: false,
		filterable: false,
		renderCell: (params) => params.value.map(({ name }) => name).join(', '),
		cellClassName: leasingRightsClass,
	},
	{
		field: 'suites',
		headerName: 'Suites',
		cellClassName: leasingRightsClass,
	},
	{
		field: 'squareFeet',
		headerName: 'Square Feet',
		cellClassName: leasingRightsClass,
	},
	{
		field: 'commencementDate',
		headerName: 'Commencement Date',
		valueGetter: adaptGridValueCallback((params) => {
			if (!params.value) return null;
			const convertedTime = moment(params.value, 'YYYY-MM-DD');
			return convertedTime.toDate();
		}),
		renderCell: (params) => {
			if (!params.value) return null;
			return moment(params.value, 'YYYY-MM-DD').format('MM/DD/YYYY');
		},
		filterOperators: dateOperators,
		type: 'date' as const,
		width: 175,
		cellClassName: leasingRightsClass,
	},
	{
		field: 'expirationDate',
		headerName: 'Expiration Date',
		valueGetter: adaptGridValueCallback((params) => {
			if (!params.value) return null;
			const convertedTime = moment(params.value, 'YYYY-MM-DD');
			return convertedTime.toDate();
		}),
		renderCell: (params) => {
			if (!params.value) return null;
			return moment(params.value, 'YYYY-MM-DD').format('MM/DD/YYYY');
		},
		filterOperators: dateOperators,
		type: 'date' as const,
		width: 125,
		cellClassName: leasingRightsClass,
	},
	{
		field: 'extensionOptions',
		headerName: 'Extension Options',
		width: 175,
		cellClassName: leasingRightsClass,
	},
	{
		field: 'spaceType',
		headerName: 'Space Type',
		type: 'singleSelect' as const,
		valueOptions: ['Reserved', 'Unreserved'],
		valueGetter: adaptGridValueCallback((params) => {
			if (params.value) {
				return formatEnumValueForDisplay(params.value);
			}
			return '';
		}),
		width: 125,
		cellClassName: leasingCountAndRatesClass,
	},
	{
		field: 'rateName',
		headerName: 'Rate Name',
		width: 125,
		cellClassName: leasingCountAndRatesClass,
	},
	{
		field: 'sublotName',
		headerName: 'Sublot',
		width: 125,
		cellClassName: leasingCountAndRatesClass,
	},
	{
		field: 'minCount',
		headerName: 'Min (Must Take)',
		sortable: false,
		filterable: false,
		cellClassName: leasingCountAndRatesClass,
		renderCell: (params) => {
			if (params.value == null) return null;
			return params.value || null;
		},
	},
	{
		field: 'maxCount',
		headerName: 'Max (Rights To)',
		sortable: false,
		filterable: false,
		cellClassName: leasingCountAndRatesClass,
	},

	{
		field: 'enforceMaxCount',
		headerName: 'Max (Rights To) - Enforce Limit',
		width: 150,
		sortable: false,
		filterable: false,
		cellClassName: leasingCountAndRatesClass,
		renderCell: (params) =>
			params.value ? <GridCheckIcon fontSize='small' /> : <GridCloseIcon fontSize='small' />,
	},
	{
		field: 'rightToConvert',
		headerName: 'Right To Convert',
		width: 150,
		sortable: false,
		filterable: false,
		cellClassName: leasingCountAndRatesClass,
		renderCell: (params) =>
			params.value ? <GridCheckIcon fontSize='small' /> : <GridCloseIcon fontSize='small' />,
	},
	{
		field: 'hasConversionRights',
		headerName: 'Conversion Rights?',
		type: 'singleSelect' as const,
		valueOptions: [
			{ value: true, label: 'Yes' },
			{ value: false, label: 'No' },
		],
		filterable: false,
		sortable: false,
		width: 200,
		cellClassName: leasingConversionRightsClass,
		renderCell: (params) => <ConversionRightsCell {...params} />,
	},
];
